import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Delete-with-dependents dialog. Opens when DELETE /api/tasks/{id} returns 409.
 * Copy is action-consequence: user knows exactly what each choice does.
 *
 * Actions (backend `on_dependents` param):
 *  - "activate" → "Aktiviraj ih sada"
 *  - "unlink"   → "Ukloni ovaj prethodni korak"
 *  - "cancel"   → "Odustani"
 *
 * If a dependent still has other pending upstreams, we say so explicitly —
 * we do NOT promise instant readiness.
 */
export default function DeleteWithDependentsDialog({
  open,
  onOpenChange,
  taskId,
  taskTitle,
  dependents = [],           // [{id, title, waiting_for_task_ids?, activated_at?}]
  dependentsMeta = {},        // {[dep.id]: {pending_others: number}}
  onDeleted,
}) {
  const [action, setAction] = useState(null);   // "activate" | "unlink" | null
  const [saving, setSaving] = useState(false);

  const run = async (choice) => {
    if (choice === "cancel") return onOpenChange(false);
    setAction(choice);
    setSaving(true);
    try {
      await api.delete(`/tasks/${taskId}?on_dependents=${choice}`);
      toast.success(choice === "activate"
        ? "Obrisano. Sledeći koraci su aktivirani gde je moglo."
        : "Obrisano. Uslov je uklonjen sa narednih zadataka.");
      onOpenChange(false);
      onDeleted?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSaving(false); setAction(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="delete-dependents-dialog"
        className="bg-white max-w-md max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-ochre" />
            Ovaj zadatak je prethodni korak za druge
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm text-ink">
            „{taskTitle}" još nije obrisan jer{" "}
            <b>{dependents.length === 1 ? "jedan zadatak čeka na njega" :
                 dependents.length >= 2 && dependents.length <= 4
                 ? `${dependents.length} zadatka čekaju na njega`
                 : `${dependents.length} zadataka čeka na njega`}</b>:
          </p>

          <ul className="space-y-1.5 bg-surface-secondary rounded-md p-3 border border-line">
            {dependents.map((d) => {
              const others = dependentsMeta[d.id]?.pending_others ?? null;
              return (
                <li
                  key={d.id}
                  data-testid={`dep-item-${d.id}`}
                  className="text-sm text-ink flex items-start gap-2"
                >
                  <span className="text-ink-muted mt-1">·</span>
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{d.title}</div>
                    {others > 0 && (
                      <div className="text-[11px] text-ink-muted">
                        (ima još {others === 1 ? "1 nezavršen prethodni korak" :
                                  others >= 2 && others <= 4
                                  ? `${others} nezavršena prethodna koraka`
                                  : `${others} nezavršenih prethodnih koraka`})
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="space-y-2 text-sm text-ink">
            <div className="rounded-md border border-line p-3 bg-white">
              <div className="font-medium">Aktiviraj ih sada</div>
              <div className="text-xs text-ink-muted mt-0.5">
                Zadaci koji čekaju samo na ovaj korak postaju spremni odmah.
                {Object.values(dependentsMeta).some((m) => (m.pending_others ?? 0) > 0) && (
                  <>{" "}Zadaci sa drugim nezavršenim koracima ostaju da čekaju te druge korake.</>
                )}
              </div>
            </div>
            <div className="rounded-md border border-line p-3 bg-white">
              <div className="font-medium">Ukloni ovaj prethodni korak</div>
              <div className="text-xs text-ink-muted mt-0.5">
                Skida vezu sa svakog narednog zadatka. Ako nema drugih koraka, oni odmah postaju spremni.
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 justify-end pt-2">
            <button
              className="btn-ghost"
              onClick={() => run("cancel")}
              data-testid="delete-dep-cancel-btn"
              disabled={saving}
            >
              Odustani
            </button>
            <button
              className="btn-ghost"
              onClick={() => run("unlink")}
              data-testid="delete-dep-unlink-btn"
              disabled={saving}
            >
              {saving && action === "unlink" && <Loader2 className="w-4 h-4 animate-spin" />}
              Ukloni ovaj prethodni korak
            </button>
            <button
              className="btn-primary"
              onClick={() => run("activate")}
              data-testid="delete-dep-activate-btn"
              disabled={saving}
            >
              {saving && action === "activate" && <Loader2 className="w-4 h-4 animate-spin" />}
              Aktiviraj ih sada
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
